'use client';

import { useMemo, useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

// Turn a character offset into a 1-based line/column for a friendly error.
function positionToLineColumn(text: string, position: number): { line: number; column: number } {
  const upto = text.slice(0, Math.max(0, position));
  const lines = upto.split('\n');
  return { line: lines.length, column: lines[lines.length - 1].length + 1 };
}

// Recursively tally object keys and primitive (leaf) values in parsed JSON.
function countNodes(value: unknown): { keys: number; values: number } {
  let keys = 0;
  let values = 0;

  const walk = (node: unknown) => {
    if (Array.isArray(node)) {
      for (const item of node) {
        walk(item);
      }
    } else if (node !== null && typeof node === 'object') {
      for (const [, child] of Object.entries(node as Record<string, unknown>)) {
        keys += 1;
        walk(child);
      }
    } else {
      values += 1;
    }
  };

  walk(value);
  return { keys, values };
}

type JsonResult =
  | { state: 'empty' }
  | { state: 'valid'; keys: number; values: number; rootType: string }
  | { state: 'error'; message: string };

function analyzeJson(input: string): JsonResult {
  if (!input.trim()) {
    return { state: 'empty' };
  }
  try {
    const parsed: unknown = JSON.parse(input);
    const { keys, values } = countNodes(parsed);
    const rootType = Array.isArray(parsed) ? 'array' : parsed === null ? 'null' : typeof parsed;
    return { state: 'valid', keys, values, rootType };
  } catch (error) {
    let message = error instanceof Error ? error.message : 'Invalid JSON.';
    const match = /position (\d+)/.exec(message);
    if (match) {
      const { line, column } = positionToLineColumn(input, Number(match[1]));
      message = `${message} (line ${line}, column ${column})`;
    }
    return { state: 'error', message };
  }
}

const SAMPLE_JSON = `{
  "name": "Cleanor",
  "live": true,
  "tools": ["srt-to-vtt", "vtt-to-srt"],
  "count": 2
}`;

export default function BrowserJsonEditor() {
  const [input, setInput] = useState<string>(SAMPLE_JSON);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const result = useMemo(() => analyzeJson(input), [input]);
  const isValid = result.state === 'valid';

  function handlePretty() {
    try {
      setInput(JSON.stringify(JSON.parse(input), null, 2));
      setStatusMessage('Pretty-printed with 2-space indentation.');
    } catch {
      setStatusMessage('Cannot format: the JSON is not valid yet.');
    }
  }

  function handleMinify() {
    try {
      setInput(JSON.stringify(JSON.parse(input)));
      setStatusMessage('Minified to a single line.');
    } catch {
      setStatusMessage('Cannot minify: the JSON is not valid yet.');
    }
  }

  async function handleCopy() {
    try {
      await copyText(input);
      setStatusMessage('Copied JSON to clipboard.');
    } catch {
      setStatusMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Client-side</span>
        <h2 className="tool-live-title">JSON editor</h2>
        <p className="tool-live-subtitle">
          Paste and edit JSON with live validation, pretty-print and minify, and a running key and
          value count. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>JSON</span>
              <textarea
                onChange={(event) => setInput(event.target.value)}
                rows={18}
                spellCheck={false}
                value={input}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!isValid}
              onClick={handlePretty}
              type="button"
            >
              Pretty-print
            </button>
            <button
              className="button button-secondary"
              disabled={!isValid}
              onClick={handleMinify}
              type="button"
            >
              Minify
            </button>
            <button
              className="button button-secondary"
              disabled={!input.trim()}
              onClick={handleCopy}
              type="button"
            >
              Copy
            </button>
          </div>
          {result.state === 'error' ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              Invalid JSON: {result.message}
            </p>
          ) : null}
          {statusMessage ? <p className="status-message status-success">{statusMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Status</span>
            <textarea
              readOnly
              rows={6}
              value={
                result.state === 'valid'
                  ? `Valid JSON\nRoot type: ${result.rootType}\nKeys: ${result.keys}\nValues: ${result.values}`
                  : result.state === 'error'
                    ? `Invalid JSON\n${result.message}`
                    : 'Paste JSON to validate.'
              }
            />
          </label>
          <p className="tool-live-panel-note">Validated locally in your browser as you type.</p>
        </section>
      </div>
    </div>
  );
}
